/**
 * @engine/vfx - VFX Graph Validator
 *
 * Validates VFX graphs for correctness before compilation.
 */

import type {
  ValidationResult,
  GraphDiagnostic,
} from '@engine/graph';
import type { VFXDataType, VFXNodeDefinition } from './types';
import { VFX_IMPLICIT_CONVERSIONS, VFX_NUMERIC_TYPES } from './types';
import { vfxNodeRegistry } from './VFXNodeRegistry';
import type { VFXGraph } from './VFXGraph';

/**
 * Check if two VFX types are compatible.
 */
export function canConnectVFXPorts(
  fromType: VFXDataType,
  toType: VFXDataType
): boolean {
  // Same type always ok
  if (fromType === toType) return true;

  // Flow only connects to flow
  if (fromType === 'flow' || toType === 'flow') {
    return fromType === 'flow' && toType === 'flow';
  }

  // Check implicit conversions
  const conversions = VFX_IMPLICIT_CONVERSIONS.get(toType);
  if (conversions?.includes(fromType)) return true;

  // Numeric promotion rules
  if (VFX_NUMERIC_TYPES.has(fromType) && VFX_NUMERIC_TYPES.has(toType)) {
    // float can accept int/uint
    if (toType === 'float' && (fromType === 'int' || fromType === 'uint')) return true;
    // vec types can accept lower dimensions
    if (toType === 'vec3' && (fromType === 'vec2' || fromType === 'float')) return true;
    if (toType === 'vec4' && (fromType === 'vec2' || fromType === 'vec3' || fromType === 'float')) return true;
  }

  // color ↔ vec4 interconvertible
  if ((fromType === 'color' && toType === 'vec4') ||
      (fromType === 'vec4' && toType === 'color')) {
    return true;
  }

  return false;
}

/**
 * VFX Graph Validator.
 *
 * Validates:
 * - Node types exist
 * - Context constraints
 * - Port type compatibility
 * - Flow connections
 */
export class VFXGraphValidator {
  /**
   * Validate a VFX graph.
   */
  validateVFXGraph(graph: VFXGraph): ValidationResult {
    const diagnostics: GraphDiagnostic[] = [];

    // Node validation
    this._validateNodes(graph, diagnostics);

    // Edge validation
    this._validateEdges(graph, diagnostics);

    // Context validation
    this._validateContexts(graph, diagnostics);

    // Flow validation
    this._validateFlowConnections(graph, diagnostics);

    // Required node validation
    this._validateRequiredNodes(graph, diagnostics);

    return {
      valid: diagnostics.every((d) => d.severity !== 'error'),
      diagnostics,
    };
  }

  /**
   * Validate node types exist.
   */
  private _validateNodes(graph: VFXGraph, diagnostics: GraphDiagnostic[]): void {
    for (const node of graph.getNodes()) {
      const def = vfxNodeRegistry.get(node.type);
      if (!def) {
        diagnostics.push({
          severity: 'error',
          message: `Unknown node type: ${node.type}`,
          nodeId: node.id,
        });
      }
    }
  }

  /**
   * Validate edges (port existence and type compatibility).
   */
  private _validateEdges(graph: VFXGraph, diagnostics: GraphDiagnostic[]): void {
    for (const edge of graph.getEdges()) {
      const fromNode = graph.getNode(edge.from.nodeId);
      const toNode = graph.getNode(edge.to.nodeId);

      if (!fromNode || !toNode) {
        diagnostics.push({
          severity: 'error',
          message: `Edge references missing node`,
          edgeId: edge.id,
        });
        continue;
      }

      const fromDef = vfxNodeRegistry.get(fromNode.type);
      const toDef = vfxNodeRegistry.get(toNode.type);
      if (!fromDef || !toDef) continue;

      const fromPort = fromDef.outputs.find((p) => p.id === edge.from.portId);
      const toPort = toDef.inputs.find((p) => p.id === edge.to.portId);

      if (!fromPort) {
        diagnostics.push({
          severity: 'error',
          message: `Output port "${edge.from.portId}" not found on "${fromNode.type}"`,
          nodeId: fromNode.id,
          portId: edge.from.portId,
        });
        continue;
      }

      if (!toPort) {
        diagnostics.push({
          severity: 'error',
          message: `Input port "${edge.to.portId}" not found on "${toNode.type}"`,
          nodeId: toNode.id,
          portId: edge.to.portId,
        });
        continue;
      }

      // Check type compatibility
      if (!canConnectVFXPorts(fromPort.type as VFXDataType, toPort.type as VFXDataType)) {
        diagnostics.push({
          severity: 'error',
          message: `Type mismatch: cannot connect ${fromPort.type} to ${toPort.type}`,
          edgeId: edge.id,
        });
      }
    }
  }

  /**
   * Validate context constraints.
   */
  private _validateContexts(graph: VFXGraph, diagnostics: GraphDiagnostic[]): void {
    for (const ctx of graph.contexts) {
      const nodes = graph.getNodesInContext(ctx.id);

      for (const node of nodes) {
        const def = vfxNodeRegistry.get(node.type);
        if (!def) continue;

        // Check if node is allowed in this context
        if (def.contexts.length > 0) {
          const contextType = ctx.type.replace('vfx.', '') as
            | 'spawn'
            | 'initialize'
            | 'update'
            | 'output';
          if (!def.contexts.includes(contextType)) {
            diagnostics.push({
              severity: 'error',
              message: `Node "${def.name}" cannot be used in ${ctx.type} context`,
              nodeId: node.id,
            });
          }
        }
      }
    }

    // Check for nodes without context
    for (const node of graph.getNodes()) {
      if (!node.contextId) {
        diagnostics.push({
          severity: 'warning',
          message: `Node "${node.type}" is not assigned to any context`,
          nodeId: node.id,
        });
      }
    }
  }

  /**
   * Validate flow connections form valid execution paths.
   */
  private _validateFlowConnections(graph: VFXGraph, diagnostics: GraphDiagnostic[]): void {
    for (const ctx of graph.contexts) {
      const contextNodes = graph.getNodesInContext(ctx.id);
      if (contextNodes.length === 0) continue;

      // Find entry points (nodes with unconnected flow input)
      const entryNodes = contextNodes.filter((node) => {
        const def = vfxNodeRegistry.get(node.type);
        if (!def) return false;
        const flowInputs = def.inputs.filter((p) => p.kind === 'flow');
        if (flowInputs.length === 0) return false;

        // Check if any flow input is connected
        const hasIncomingFlow = graph.getEdges().some(
          (e) =>
            e.to.nodeId === node.id &&
            flowInputs.some((p) => p.id === e.to.portId)
        );
        return !hasIncomingFlow;
      });

      if (entryNodes.length > 1) {
        diagnostics.push({
          severity: 'warning',
          message: `Context "${ctx.type}" has multiple unconnected entry points`,
        });
      }
    }
  }

  /**
   * Validate required nodes are present.
   */
  private _validateRequiredNodes(graph: VFXGraph, diagnostics: GraphDiagnostic[]): void {
    // Each VFX graph should have at least one spawn node
    const spawnContext = graph.getContextByType('spawn');
    if (spawnContext) {
      const spawnNodes = graph.getNodesInContext(spawnContext.id);
      const hasSpawnSource = spawnNodes.some((n) => n.type.startsWith('vfx.spawn.'));
      if (!hasSpawnSource) {
        diagnostics.push({
          severity: 'warning',
          message: 'No spawn source defined. Particles will not spawn.',
        });
      }
    }

    // Each VFX graph should have at least one output node
    const outputContext = graph.getContextByType('output');
    if (outputContext) {
      const outputNodes = graph.getNodesInContext(outputContext.id);
      const hasOutput = outputNodes.some((n) => n.type.startsWith('vfx.output.'));
      if (!hasOutput) {
        diagnostics.push({
          severity: 'warning',
          message: 'No output defined. Particles will not be rendered.',
        });
      }
    }
  }

  /**
   * Get compatible types for a port.
   */
  getCompatibleTypes(type: VFXDataType): VFXDataType[] {
    const compatible: Set<VFXDataType> = new Set([type]);

    // Add implicit conversions
    const conversions = VFX_IMPLICIT_CONVERSIONS.get(type);
    if (conversions) {
      for (const t of conversions) {
        compatible.add(t);
      }
    }

    // Add vec lower dimensions
    if (type === 'vec3') {
      compatible.add('vec2');
      compatible.add('float');
    }
    if (type === 'vec4') {
      compatible.add('vec3');
      compatible.add('vec2');
      compatible.add('float');
      compatible.add('color');
    }
    if (type === 'color') {
      compatible.add('vec4');
      compatible.add('vec3');
    }

    return [...compatible];
  }
}

/**
 * Global VFX validator instance.
 */
export const vfxValidator = new VFXGraphValidator();
